import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { EllipsisVertical } from "lucide-react";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import type { SortState } from "./AdminTableControls";

export type AdminDataTableColumn<T, K extends string> = {
  id: string;
  header: string;
  sortKey?: K;
  width?: string;
  align?: "left" | "right";
  nowrap?: boolean;
  className?: string;
  render: (row: T) => ReactNode;
};

export function AdminDataTable<T, K extends string>({
  columns,
  rows,
  getRowKey,
  sort,
  onSort,
  minWidth = 960,
  renderExpandedRow,
}: {
  columns: Array<AdminDataTableColumn<T, K>>;
  rows: T[];
  getRowKey: (row: T) => string;
  sort?: SortState<K>;
  onSort?: (key: K) => void;
  minWidth?: number;
  renderExpandedRow?: (row: T) => ReactNode;
}) {
  return (
    <div className="admin-table-scroll">
      <style>{tableCss}</style>
      <table className="admin-data-table" style={{ minWidth }}>
        <colgroup>
          {columns.map((column) => (
            <col key={column.id} style={column.width ? { width: column.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column) => {
              const active = Boolean(sort && column.sortKey && sort.key === column.sortKey);
              return (
                <th key={column.id} style={{ textAlign: column.align ?? "left" }}>
                  {column.sortKey && sort && onSort ? (
                    <button
                      type="button"
                      className="admin-sort-button"
                      onClick={() => onSort(column.sortKey!)}
                    >
                      <span>{column.header}</span>
                      <span className={active ? "admin-sort-mark active" : "admin-sort-mark"}>
                        {active ? sort.direction : ""}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <FragmentRows
              key={getRowKey(row)}
              columns={columns}
              row={row}
              renderExpandedRow={renderExpandedRow}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FragmentRows<T, K extends string>({
  columns,
  row,
  renderExpandedRow,
}: {
  columns: Array<AdminDataTableColumn<T, K>>;
  row: T;
  renderExpandedRow?: (row: T) => ReactNode;
}) {
  const expanded = renderExpandedRow?.(row);
  return (
    <>
      <tr>
        {columns.map((column) => (
          <td
            key={column.id}
            className={column.className}
            style={{
              textAlign: column.align ?? "left",
              whiteSpace: column.nowrap ? "nowrap" : undefined,
            }}
          >
            {column.render(row)}
          </td>
        ))}
      </tr>
      {expanded}
    </>
  );
}

export type AdminRowAction = {
  label: string;
  to?: string;
  onSelect?: () => void;
};

export function AdminRowActions({
  label,
  actions,
}: {
  label: string;
  actions: AdminRowAction[];
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function closeOnOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    function updatePosition() {
      setRect(buttonRef.current?.getBoundingClientRect() ?? null);
    }
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="admin-row-actions-trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setRect(buttonRef.current?.getBoundingClientRect() ?? null);
          setOpen((current) => !current);
        }}
      >
        <EllipsisVertical size={17} aria-hidden="true" />
      </button>
      {open && rect
        ? createPortal(
            <div
              ref={menuRef}
              className="admin-row-actions-menu"
              role="menu"
              style={{
                top: rect.bottom + 6,
                right: Math.max(12, window.innerWidth - rect.right),
              }}
            >
              {actions.map((action) =>
                action.to ? (
                  <Link
                    key={action.label}
                    to={action.to}
                    className="admin-row-actions-item"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                  >
                    {action.label}
                  </Link>
                ) : (
                  <button
                    key={action.label}
                    type="button"
                    className="admin-row-actions-item"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      action.onSelect?.();
                    }}
                  >
                    {action.label}
                  </button>
                ),
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

const tableCss = `
  .admin-table-scroll {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 0.4rem;
  }
  .admin-data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    background: #fff;
    border: 1.5px dashed rgba(201,147,76,0.40);
    border-radius: 16px;
  }
  .admin-data-table th,
  .admin-data-table td {
    text-align: left;
    padding: 0.7rem 0.9rem;
    border-bottom: 1px dashed rgba(201,147,76,0.18);
    font-family: ${fonts.body};
    vertical-align: middle;
  }
  .admin-data-table thead th {
    background: ${colors.blushLight};
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: rgba(8,45,61,0.7);
    border-bottom: 1px solid rgba(201,147,76,0.15);
  }
  .admin-data-table tbody tr:last-child td {
    border-bottom: none;
  }
  .admin-data-table tbody td {
    font-size: 13.5px;
    color: rgba(8,45,61,0.85);
  }
  .admin-sort-button {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    width: 100%;
    border: none;
    background: transparent;
    padding: 0;
    color: inherit;
    cursor: pointer;
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    text-align: inherit;
  }
  .admin-sort-mark {
    min-width: 26px;
    opacity: 0.35;
    font-size: 8px;
    letter-spacing: 0.04em;
  }
  .admin-sort-mark.active {
    opacity: 0.8;
  }
  .admin-row-actions-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 1px solid rgba(201,147,76,0.45);
    background: ${colors.cream};
    color: ${colors.navy};
    cursor: pointer;
  }
  .admin-row-actions-trigger:hover {
    box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35);
    color: ${colors.berryRose};
  }
  .admin-row-actions-menu {
    position: fixed;
    z-index: 1000;
    min-width: 180px;
    padding: 6px;
    border: 1px solid rgba(201,147,76,0.35);
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 18px 40px -24px rgba(8,45,61,0.55);
  }
  .admin-row-actions-item {
    display: block;
    width: 100%;
    padding: 0.55rem 0.7rem;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: ${colors.navy};
    cursor: pointer;
    text-decoration: none;
    font-family: ${fonts.body};
    font-size: 12.5px;
    font-weight: 700;
    text-align: left;
  }
  .admin-row-actions-item:hover {
    background: ${colors.blushLight};
    color: ${colors.berryRose};
  }
`;
